// src/auth/protected-route.js
import React, { useMemo } from "react";
import { Route } from "react-router-dom";
import { withAuthenticationRequired } from "@auth0/auth0-react";
import Loading from "../components/Loading/Loading";

const ProtectedRoute = ({ component: Component, render, children, ...rest }) => {
  // Memoize the guarded wrappers so we don't recreate them every render.
  const GuardedComponent = useMemo(() => {
    if (!Component) return null;
    return withAuthenticationRequired(Component, {
      onRedirecting: () => <Loading />,
    });
  }, [Component]);

  const GuardedRender = useMemo(() => {
    if (!render) return null;
    // Wrap the render fn in a component so it can be guarded
    const RenderWrapper = (routeProps) => render(routeProps);
    return withAuthenticationRequired(RenderWrapper, {
      onRedirecting: () => <Loading />,
    });
  }, [render]);

  const GuardedChildren = useMemo(() => {
    if (!children) return null;
    // Children-as-a-function or nodes: normalize to a component
    const ChildrenWrapper = (routeProps) =>
      typeof children === "function" ? children(routeProps) : children;
    return withAuthenticationRequired(ChildrenWrapper, {
      onRedirecting: () => <Loading />,
    });
  }, [children]);

  // Prefer component > render > children, like Route does
  if (GuardedComponent) {
    return <Route {...rest} component={GuardedComponent} />;
  }
  if (GuardedRender) {
    return <Route {...rest} component={GuardedRender} />;
  }
  if (GuardedChildren) {
    return <Route {...rest} component={GuardedChildren} />;
  }

  // Nothing provided: render a guarded no-op that shows Loading until redirect
  const Noop = () => null;
  const GuardedNoop = withAuthenticationRequired(Noop, {
    onRedirecting: () => <Loading />,
  });
  return <Route {...rest} component={GuardedNoop} />;
};

export default ProtectedRoute;